import { LegalLayout, Section, Bullets } from "../components/LegalLayout";
import { Seo } from "../components/Seo";

const openPreferences = () => window.dispatchEvent(new Event("open-cookie-preferences"));

export const CookiePolicy = () => (
  <LegalLayout
    title="Cookie Policy"
    updated="June 2026"
    intro="This Cookie Policy explains how Interlocked Solutions Ltd uses cookies and similar technologies on this website, in line with UK GDPR and the Privacy and Electronic Communications Regulations (PECR). It should be read alongside our Privacy Policy."
  >
    <Seo
      title="Cookie Policy | Interlocked Solutions"
      description="Learn how Interlocked Solutions Ltd uses cookies and similar technologies in line with UK GDPR and PECR — the categories we use, how consent works, and how to manage or withdraw your preferences at any time."
      path="/cookie-policy"
    />
    <Section heading="What are cookies?">
      <p>
        Cookies are small text files placed on your device when you visit a website. They help the
        site work, remember your preferences, and provide information to the site owners. Similar
        technologies include pixels, tags and local storage.
      </p>
    </Section>

    <Section heading="Your consent">
      <p>
        When you first visit our site, we show a cookie banner. Strictly necessary cookies are always
        active because the site cannot function without them. We only set analytics and marketing
        cookies <strong className="text-foreground">after you give consent</strong> — these are
        switched off by default. You can accept all, reject all, or choose specific categories.
      </p>
    </Section>

    <Section heading="Categories of cookies we use">
      <div className="space-y-6">
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading text-base font-medium text-foreground">1. Strictly necessary</h3>
          <p className="mt-2 text-sm">
            Essential for the website and secure areas (such as the leads dashboard login) to work.
            These include session and security cookies and your cookie-consent choice. They do not
            require consent and cannot be switched off.
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading text-base font-medium text-foreground">2. Analytics / performance</h3>
          <p className="mt-2 text-sm">
            Help us understand how visitors interact with the site (e.g. which pages are viewed) so we
            can improve it. These are only set with your consent and can be withdrawn at any time.
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading text-base font-medium text-foreground">3. Marketing</h3>
          <p className="mt-2 text-sm">
            Used to deliver relevant content and measure the effectiveness of our campaigns. These are
            only set with your consent and can be withdrawn at any time.
          </p>
        </div>
      </div>
    </Section>

    <Section heading="Managing your preferences">
      <p>You can change or withdraw your consent at any time:</p>
      <Bullets
        items={[
          "Use our cookie preferences panel — you can reopen it below.",
          "Adjust your browser settings to block or delete cookies (this may affect how the site works).",
        ]}
      />
      <button
        onClick={openPreferences}
        data-testid="reopen-cookie-preferences"
        className="mt-4 inline-flex rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-transform duration-300 hover:scale-[1.03]"
      >
        Manage cookie preferences
      </button>
    </Section>

    <Section heading="Contact">
      <p>
        Questions about our use of cookies? Email{" "}
        <a href="mailto:info@interlockedsolutions.co.uk" className="text-primary underline underline-offset-2">
          info@interlockedsolutions.co.uk
        </a>
        . You can also lodge a complaint with the UK Information Commissioner’s Office (ICO) at{" "}
        <a
          href="https://ico.org.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2"
        >
          ico.org.uk
        </a>
        .
      </p>
    </Section>
  </LegalLayout>
);
